import { useState, useEffect, useCallback } from 'react';
import { Ingredient } from '../types';

const STORAGE_KEY = 'tracker_ingredients_edit';

interface UseGlobalIngredientsReturn {
  ingredients: Ingredient[];
  updateIngredients: (
    newIngredients: Ingredient[] | ((prev: Ingredient[]) => Ingredient[])
  ) => void;
  setIngredients: (ingredients: Ingredient[]) => void;
}

export const useGlobalIngredientsEdit = (
  initialIngredients: Ingredient[]
): UseGlobalIngredientsReturn => {
  // Initialize from sessionStorage
  const [ingredients, setIngredients] = useState<Ingredient[]>(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    const shouldUseData =
      sessionStorage.getItem('tracker_should_use_data_from_session_storage') === 'true';

    console.log('shouldUseData', shouldUseData);

    // If shouldUseData is true, prioritize sessionStorage
    if (shouldUseData && stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error('Error parsing stored ingredients:', error);
        return initialIngredients;
      }
    }

    // Try to use stored ingredients
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error('Error parsing stored ingredients:', error);
        return initialIngredients;
      }
    }
    return initialIngredients;
  });

  // Listen for custom storage changes from other components
  useEffect(() => {
    const handleStorageChange = (e: CustomEvent) => {
      if (e.detail && e.detail.key === STORAGE_KEY) {
        try {
          const parsedIngredients = JSON.parse(e.detail.newValue);
          setIngredients(parsedIngredients);
        } catch (error) {
          console.error('Error parsing storage change:', error);
        }
      }
    };

    window.addEventListener('ingredientsChanged', handleStorageChange as EventListener);
    return () =>
      window.removeEventListener('ingredientsChanged', handleStorageChange as EventListener);
  }, []);

  const updateIngredients = useCallback(
    (newIngredients: Ingredient[] | ((prev: Ingredient[]) => Ingredient[])) => {
      setIngredients((prevIngredients) => {
        // Support functional updates
        const ingredientsToSet =
          typeof newIngredients === 'function' ? newIngredients(prevIngredients) : newIngredients;

        // Save to sessionStorage
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ingredientsToSet));
        // Dispatch custom event to notify other components
        const event = new CustomEvent('ingredientsChanged', {
          detail: {
            key: STORAGE_KEY,
            newValue: JSON.stringify(ingredientsToSet),
          },
        });
        window.dispatchEvent(event);

        return ingredientsToSet;
      });
    },
    []
  );

  return {
    ingredients,
    updateIngredients,
    setIngredients,
  };
};
